import { createContext, useState, useEffect } from "react";
import axios from "axios";
const BASE_URL = process.env.REACT_APP_BASE_URL;


export const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]); // 🔔 Notifications state
    const [unreadCount, setUnreadCount] = useState(0); // 🔴 Unread notifications count
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showPinModal, setShowPinModal] = useState(false);


    // 🌙 Dark mode state
    const [isDarkMode, setIsDarkMode] = useState(
        localStorage.getItem("theme") === "dark"
    );

    // Apply dark mode to document
    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add("dark-theme");
            localStorage.setItem("theme", "dark");
        } else {
            document.documentElement.classList.remove("dark-theme");
            localStorage.setItem("theme", "light");
        }
    }, [isDarkMode]);


    // Set auth token in headers
    const setAuthToken = (token) => {
        if (token) {
            axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        } else {
            delete axios.defaults.headers.common["Authorization"];
        }
    };




    // Fetch user data from the backend
    const fetchUserData = async () => {
        const token = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");

        if (!token || !storedUser) {
            setLoading(false);
            return;
        }

        setAuthToken(token);
        setCurrentUser(JSON.parse(storedUser)); // ✅ Load user from localStorage
        await fetchNotifications(); // 🔔 Fetch notifications
        setLoading(false);
    };

    // ✅ Fetch Notifications
    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${BASE_URL}/notifications`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setNotifications(res.data);
            // Count unread notifications
            const unread = res.data.filter(notification => !notification.isRead).length;
            setUnreadCount(unread);
        } catch (error) {
            console.error("❌ Failed to fetch notifications:", error);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem("token");


            await axios.put(`${BASE_URL}/notifications/read`, { notificationId }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            // Update state locally to mark as read
            setNotifications(prev =>
                prev.map(notification =>
                    notification._id === notificationId ? { ...notification, isRead: true } : notification
                )
            );
            setUnreadCount(prev => Math.max(prev - 1, 0));
        } catch (error) {
            console.error("❌ Failed to mark notification as read:", error);
        }
    };

    useEffect(() => {
        console.log("🔁 useEffect: Fetching Authcontext notifications");
        fetchNotifications(); // on mount
    }, []);

    useEffect(() => {
        console.log("🔁 useEffect: Fetching Authcontext userdata");
        fetchUserData();

        // ✅ Add Axios interceptor to detect expired tokens
        const interceptor = axios.interceptors.response.use(
            (response) => response, // ✅ Allow successful responses
            (error) => {
                if (error.response?.status === 401 || error.response?.status === 403) {
                    console.error("❌ Token expired or invalid! Logging out...");
                    logout(); // ✅ Auto logout on token expiration
                }
                return Promise.reject(error);
            }
        );

        return () => axios.interceptors.response.eject(interceptor); // ✅ Cleanup on unmount
    }, []);

    useEffect(() => {
        if (currentUser?.hasTransactionPin === false) {
            setShowPinModal(true);
        } else {
            setShowPinModal(false);
        }
    }, [currentUser?.hasTransactionPin]);



    const register = async (inputs) => {
        try {
            const res = await axios.post(`${BASE_URL}/register`, inputs);

            return {
                status: "otp_sent",
                email: res.data.email,
                message: "Account created. Please verify your email with the OTP."
            };
        } catch (error) {
            console.error("REGISTRATION ERROR:", error.response?.data || error.message || error);
            return {
                error: error.response?.data?.message || "Registration failed"
            };
        }
    };


    const login = async (inputs) => {
        try {
            // Send the login request to the backend
            const res = await axios.post(`${BASE_URL}/login`, inputs);

            // **If the login is successful and the user is unverified**
            if (res.data.status === "unverified") {
                console.log("User is unverified, need to redirect to OTP verification.");
                return {
                    status: "unverified",
                    email: res.data.email,  // Send back the email to auto-fill the OTP page
                    message: "Account not verified. Please check your email for the OTP.",
                };
            }

            // **Successful login response: User is verified**
            // Store user info and token in localStorage and context
            setCurrentUser(res.data.user);
            localStorage.setItem("user", JSON.stringify(res.data.user));
            localStorage.setItem("token", res.data.token);
            setAuthToken(res.data.token);
            fetchNotifications();

            return { success: true };
        } catch (error) {
            // **Handle the error response from the server**
            if (error.response) {
                // If user not found or password is incorrect (error.response.message will contain this)
                if (error.response.data.message === "Incorrect password" || error.response.data.message === "User not found") {
                    console.log("Login failed - invalid credentials.");
                    return {
                        error: error.response.data.message || "Invalid email or password.",
                    };
                }

                // Other possible errors (e.g., server errors)
                return { error: error.response?.data?.message || "Login failed" };
            }

            // If it's a network error or no response from the server
            console.log("Network error or no response:", error);
            return { error: "Network error or no response from server" };
        }
    };



    const requestPasswordResetOtp = async (email) => {
        try {
            console.log("🔐 Requesting OTP for password reset:", email);

            const res = await axios.post(`${BASE_URL}/request-password-reset`, { email });

            console.log("✅ OTP sent for password reset:", res.data);

            return { success: true, message: res.data.message };
        } catch (error) {
            console.error("❌ Request OTP failed:", error.response?.data || error.message);
            return {
                error: error.response?.data?.message || "Failed to request OTP"
            };
        }
    };


    const resetPasswordWithOtp = async (email, otpCode, newPassword) => {
        try {
            console.log("🔐 Resetting password via OTP for:", email);

            const res = await axios.post(`${BASE_URL}/reset-password`, {
                email,
                otpCode,
                newPassword
            });

            console.log("✅ Password reset via OTP successful:", res.data);

            const user = res.data.user;
            const token = res.data.token;

            setCurrentUser(user);
            localStorage.setItem("user", JSON.stringify(user));
            localStorage.setItem("token", token);
            setAuthToken(token);

            return { success: true, message: res.data.message };
        } catch (error) {
            console.error("❌ OTP password reset failed:", error.response?.data || error.message);
            return {
                error: error.response?.data?.message || "Failed to reset password"
            };
        }
    };



    const verifyOtp = async (email, otp) => {
        try {
            console.log("🔐 Verifying OTP for:", email, "with code:", otp);

            const res = await axios.post(`${BASE_URL}/verify-otp`, {
                phoneOrEmail: email,
                otpCode: otp
            });

            console.log("✅ OTP verification response:", res.data);

            const user = res.data.user;
            const token = res.data.token;

            setCurrentUser(user);
            localStorage.setItem("user", JSON.stringify(user));
            localStorage.setItem("token", token);
            setAuthToken(token);

            fetchNotifications(); // optional if needed

            return { success: true, message: res.data.message };
        } catch (error) {
            console.error("❌ OTP verification failed:", error.response?.data || error.message || error);
            return {
                error: error.response?.data?.message || "OTP verification failed"
            };
        }
    };

    const resendOtp = async (email) => {
        try {
            console.log("🔄 Requesting OTP resend for:", email);

            const res = await axios.post(`${BASE_URL}/resend-otp`, { email });

            console.log("✅ OTP resend response:", res.data);

            return { success: true, message: res.data.message };
        } catch (error) {
            const errMsg =
                error.response?.data?.message || "Failed to resend OTP";

            console.error("❌ OTP resend error:", error.response?.data || error.message || error);

            return { error: errMsg };
        }
    };


    const sendOtpForTransactionPinReset = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(`${BASE_URL}/send-reset-pin-otp`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return { success: true, message: res.data.message };
        } catch (err) {
            console.error("Failed to send OTP for PIN reset", err);
            return {
                success: false,
                error: err.response?.data?.message || "Failed to send OTP",
            };
        }
    };

    const resetTransactionPinWithOtp = async (otpCode, newPin) => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(`${BASE_URL}/reset-pin-with-otp`, {
                otpCode,
                newPin,
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // Optionally update user state if needed
            const updatedUser = {
                ...currentUser,
                hasTransactionPin: true,
            };

            setCurrentUser(updatedUser);
            localStorage.setItem("user", JSON.stringify(updatedUser));

            return { success: true, message: res.data.message };
        } catch (err) {
            console.error("Failed to reset PIN with OTP", err);
            return {
                success: false,
                error: err.response?.data?.message || "Failed to reset PIN",
            };
        }
    };



    const setTransactionPin = async (pin) => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(`${BASE_URL}/set-transaction-pin`, { pin }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            // Update user state to reflect pin is now set
            const updatedUser = {
                ...currentUser,
                hasTransactionPin: true
            };

            setCurrentUser(updatedUser);
            localStorage.setItem("user", JSON.stringify(updatedUser));

            return { success: true, message: res.data.message };
        } catch (err) {
            console.error("Failed to set PIN", err);
            return {
                success: false,
                error: err.response?.data?.message || "Failed to set transaction PIN"
            };
        }
    };

    const verifyTransactionPin = async (pin) => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(`${BASE_URL}/verify-transaction-pin`, { pin }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return { success: true, message: res.data.message };
        } catch (err) {
            console.error("Failed to verify PIN", err);
            return {
                success: false,
                error: err.response?.data?.message || "Failed to verify transaction PIN",
            };
        }
    };

    const changeTransactionPin = async (currentPin, newPin) => {
        try {
            const token = localStorage.getItem("token");

            const res = await axios.post(
                `${BASE_URL}/change-transaction-pin`,
                { currentPin, newPin },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return { success: true, message: res.data.message };
        } catch (err) {
            console.error("Failed to change transaction PIN", err);
            return {
                success: false,
                error: err.response?.data?.message || "Failed to change transaction PIN",
            };
        }
    };



    const reportBug = async (issue, files) => {
        try {
            const formData = new FormData();
            formData.append('issue', issue);
            files.forEach((file) => {
                formData.append('files', file);
            });

            const res = await axios.post(`${BASE_URL}/report-bug`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            console.log('Bug Report Response:', res.data); // ✅ SHOW RESPONSE

            return res.data;
        } catch (error) {
            console.error('Bug Report Error:', error);
            return { success: false, error: error?.response?.data?.error || 'Something went wrong' };
        }
    };


    const updateUserProfile = async (updatedData, profileImage) => {
        try {
            const formData = new FormData();

            // ✅ Append text fields to formData
            Object.keys(updatedData).forEach((key) => {
                if (updatedData[key]) {
                    formData.append(key, updatedData[key]);
                }
            });

            // ✅ Append profile image if provided
            if (profileImage) {
                formData.append("profileImage", profileImage);
            }

            console.log("🔄 Sending Profile Update Request...");
            console.log("📦 FormData contents:", [...formData.entries()]);

            // ✅ Send request to backend
            const res = await axios.put(`${BASE_URL}/update-profile`, formData, {
                headers: { "Content-Type": "multipart/form-data" }, // Required for file upload

            });

            console.log("✅ Profile Updated Successfully:", res.data.user);

            // ✅ Update the current user in context
            setCurrentUser(res.data.user);
            localStorage.setItem("user", JSON.stringify(res.data.user));

            return { success: true };
        } catch (error) {
            console.error("❌ Profile Update Failed:", error.response?.data || error.message);
            return { error: error.response?.data?.message || "Failed to update profile" };
        }
    };



    // Logout function
    const logout = () => {
        return new Promise((resolve) => {
            setCurrentUser(null);
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            setAuthToken(null);

            // Remove dark theme class from document root
            document.documentElement.classList.remove("dark-theme");
            document.body.classList.remove("dark-theme");

            resolve();
        });
    };


    const getTransactionById = async (id) => {
        try {
            const res = await axios.get(`${BASE_URL}/transactions/${id}`);
            return res.data;
        } catch (error) {
            console.error("❌ Error fetching transaction by ID:", error);
            return { error: error.response?.data?.message || "Transaction not found" };
        }
    };

    const fetchCurrentUser = async () => {
        const token = localStorage.getItem("token");
        if (!token) return { error: "User not logged in" };

        try {
            const res = await axios.get(`${BASE_URL}/get-user`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const user = res.data.user;

            setCurrentUser(user);
            localStorage.setItem("user", JSON.stringify(user));

            console.log("✅ User state updated:");
            return { success: true, user };

        } catch (error) {
            console.error("❌ Failed to fetch user:", error.response?.data);
            return {
                error: error.response?.data?.message || "Failed to fetch user",
            };
        }
    };


    const addFunds = async (amount, reference) => {
        return await fetchCurrentUser(); // 🔄 Just call your reusable user refresher
    };


    const getBanks = async () => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            const res = await axios.get(`${BASE_URL}/banks`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            console.log("✅ Banks Data fetched successfully:", res.data);
            return { banks: res.data.banks };
        }
        catch (error) {
            console.error("❌ Failed to fetch banks:", error.response?.data);
            return { error: error.response?.data?.message || "Failed to fetch banks" };
        }
    };

    const getPalmPayBanks = async () => {
        const token = localStorage.getItem("token");
        const currentUser = JSON.parse(localStorage.getItem("user"));

        if (!token || !currentUser) {
            return { error: "User not authenticated" };
        }

        try {
            console.log("📤 Fetching PalmPay bank list...");

            const res = await axios.get(`${BASE_URL}/palmpay-banks`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("✅ PalmPay banks fetched successfully:", res.data);

            return { banks: res.data.banks };
        } catch (error) {
            console.error("❌ Failed to fetch PalmPay banks:", error.response?.data || error.message);

            return {
                error: error.response?.data?.message || "Failed to fetch PalmPay banks",
            };
        }
    };

    const queryBankAccountName = async (payeeBankCode, payeeBankAccNo) => {
        const token = localStorage.getItem("token");
        const currentUser = JSON.parse(localStorage.getItem("user"));

        if (!token || !currentUser) {
            return { error: "User not authenticated" };
        }

        try {
            console.log("📦 Querying account name with:", { payeeBankCode, payeeBankAccNo });
            const res = await axios.post(
                `${BASE_URL}/query-bank-account-name`,
                { payeeBankCode, payeeBankAccNo },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            console.log("✅ Account name fetched:", res.data);
            return { accountName: res.data.accountName };

        } catch (error) {
            const backendError = error.response?.data;
            console.error("❌ Failed to fetch account name:", backendError || error.message);

            return {
                error: backendError?.message || backendError?.error || "Failed to fetch account name",
            };
        }
    };



    const addBankAccount = async (accountName, bankName, accountNumber, bankCode) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Adding bank account with:", { accountName, bankName, accountNumber, bankCode });

            const res = await axios.post(
                `${BASE_URL}/add-bank-account`, // your backend route for adding bank accounts
                { accountName, bankName, accountNumber, bankCode },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            console.log("✅ Bank account added successfully:", res.data);

            return res.data;
        } catch (error) {
            console.error("❌ Failed to add bank account:", error.response?.data);
            return { error: error.response?.data?.message || "Failed to add bank account" };
        }
    };

    // 🔹 Fetch Saved Bank Accounts Function
    const fetchBankAccounts = async () => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📡 Fetching saved bank accounts...");

            const res = await axios.get(`${BASE_URL}/bank-accounts`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("✅ Bank accounts fetched:", res.data.bankAccounts);

            return { bankAccounts: res.data.bankAccounts };
        } catch (error) {
            console.error("❌ Failed to fetch bank accounts:", error.response?.data);
            return {
                error: error.response?.data?.message || "Failed to fetch bank accounts",
            };
        }
    };

    const palmPayPayout = async ({
        payeeName,
        payeeBankCode,
        payeeBankAccNo,
        payeePhoneNo,
        amount,
        currency = "NGN",
        remark = ""
    }) => {
        const token = localStorage.getItem("token");
        const currentUser = JSON.parse(localStorage.getItem("user"));

        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Sending PalmPay payout request with:", {
                payeeName,
                payeeBankCode,
                payeeBankAccNo,
                payeePhoneNo,
                amount,
                currency,
                remark
            });

            const res = await axios.post(
                `${BASE_URL}/payout`,
                {

                    payeeName,
                    payeeBankCode,
                    payeeBankAccNo,
                    payeePhoneNo,
                    amount,
                    currency,
                    remark
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const { newBalance, transaction } = res.data;

            console.log("✅ Payout successful:", res.data);

            // ✅ Update local user state
            setCurrentUser((prevUser) => {
                const updatedUser = {
                    ...prevUser,
                    balance: newBalance,
                    transactions: [...(prevUser.transactions || []), transaction],
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                return updatedUser;
            });

            return res.data;

        } catch (error) {
            console.error("❌ PalmPay payout failed:", error.response?.data);

            const transaction = error.response?.data?.transaction;
            const newBalance = error.response?.data?.newBalance;

            if (transaction) {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        transactions: [...(prevUser.transactions || []), transaction],
                    };

                    if (typeof newBalance !== "undefined") {
                        updatedUser.balance = newBalance;
                    }

                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });
            }

            return {
                error: error.response?.data?.message || "PalmPay payout failed",
            };
        }
    };


    // 🔹 Send or Resend A2C OTP
    /*
    const sendAirtimeOtp = async ({ network, senderNumber }) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📡 Sending A2C OTP...");
            const res = await axios.post("/a2c/send-otp", { network, senderNumber }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            console.log("✅ OTP sent:", res.data);
            return res.data;
        } catch (error) {
            console.error("❌ OTP sending failed:", error.response?.data);
            return {
                error: error.response?.data?.message || "Failed to send OTP",
            };
        }
    };

    // 🔹 Verify A2C OTP
    const verifyAirtimeOtp = async ({ identifier, otp }) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📡 Verifying A2C OTP...");
            const res = await axios.post("/a2c/verify-otp", { identifier, otp }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            console.log("✅ OTP verified:", res.data);
            return res.data;
        } catch (error) {
            console.error("❌ OTP verification failed:", error.response?.data);
            return {
                error: error.response?.data?.message || "Failed to verify OTP",
            };
        }
    };

    // 🔹 Send Airtime to Cash
    const sendAirtimeToCash = async ({ network, amount, quantity, pin, sessionId, reference }) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📡 Sending Airtime to Cash...");
            const res = await axios.post("/a2c/send-airtime", {
                network, amount, quantity, pin, sessionId, reference
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            console.log("✅ Airtime sent:", res.data);
            return res.data;
        } catch (error) {
            console.error("❌ Airtime to cash failed:", error.response?.data);
            return {
                error: error.response?.data?.message || "Airtime to Cash failed",
            };
        }
    };

   */



    /* const purchaseDataHouse = async (networkId, planId, phoneNumber, price) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Sending purchase request with:", {
                networkId, // Network identifier
                planId, // Plan identifier
                phoneNumber,
                price,
                type: "buy-data",  // ✅ ADD THIS
            });

            const res = await axios.post(
                "/purchase-data-house", // SME purchase endpoint
                {
                   
                    networkId, // Identifies the network (MTN, Airtel, Glo, etc.)
                    planId, // Identifies the specific plan within the network
                    phoneNumber,
                    price,
                    type: "buy-data",  // ✅ ADD THIS
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            console.log("✅ Purchase successful! Response:", res.data);

            // Update user's balance & transactions
            setCurrentUser((prevUser) => {
                const updatedUser = {
                    ...prevUser,
                    balance: res.data.newBalance,
                    transactions: [...(prevUser.transactions || []), res.data.transaction],
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                return updatedUser;
            });

            return res.data;
        } catch (error) {
            console.error("❌ Purchase failed!", error.response?.data);
            return { error: error.response?.data?.message || "Purchase failed" };
        }
    };
    */

    const getTransactions = async () => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Fetching transactions for user:", currentUser.id);

            const res = await axios.get(`${BASE_URL}/transactions`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            console.log("✅ Transactions fetched successfully:", res.data);
            return res.data;
        } catch (error) {
            console.error("❌ Failed to fetch transactions:", error.response?.data);
            return { error: error.response?.data?.message || "Failed to fetch transactions" };
        }
    };


    const buyAirtime = async (network, amount, phoneNumber) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Sending airtime purchase request:", {

                network,
                amount,
                phoneNumber,
                type: "buy-airtime",
            });

            const res = await axios.post(
                `${BASE_URL}/buy-airtime`,
                {
                    network,
                    amount,
                    phoneNumber,
                    type: "buy-airtime"
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            console.log("✅ Airtime purchase response:", res.data);

            const { newBalance, transaction } = res.data;

            // ✅ Always use balance from backend
            setCurrentUser((prevUser) => {
                const updatedUser = {
                    ...prevUser,
                    balance: newBalance,
                    transactions: [...(prevUser.transactions || []), transaction],
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                return updatedUser;
            });

            return res.data;

        } catch (error) {
            console.error("❌ Airtime purchase failed!", error.response?.data);

            const transaction = error.response?.data?.transaction;
            const newBalance = error.response?.data?.newBalance;

            // ✅ If backend still returned transaction + updated balance
            if (transaction && typeof newBalance !== "undefined") {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        balance: newBalance,
                        transactions: [...(prevUser.transactions || []), transaction],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });
            }

            return {
                error: error.response?.data?.message || "Airtime purchase failed"
            };
        }
    };



    const buyCableTV = async (smartcardNumber, phoneNumber, variationId, serviceId, amount) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Sending cable TV purchase request:", {
                smartcardNumber,
                phoneNumber,
                variationId,
                serviceId,
                amount,
            });

            const res = await axios.post(
                `${BASE_URL}/buy-cabletv`,
                {
                    phone: phoneNumber,
                    service_id: serviceId,
                    smartcard_number: smartcardNumber,
                    variation_id: variationId,
                    amount,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            console.log("✅ Cable TV purchase response:", res.data);

            const { transaction, newBalance } = res.data;

            // ✅ Update user balance and transactions from backend response
            setCurrentUser((prevUser) => {
                const updatedUser = {
                    ...prevUser,
                    balance: newBalance,
                    transactions: [...(prevUser.transactions || []), transaction],
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                return updatedUser;
            });

            return res.data;

        } catch (error) {
            console.error("❌ Cable TV purchase failed!", error.response?.data);

            const transaction = error.response?.data?.transaction;
            const newBalance = error.response?.data?.newBalance;

            // ✅ Still record failed transaction + updated balance if provided
            if (transaction && typeof newBalance !== "undefined") {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        balance: newBalance,
                        transactions: [...(prevUser.transactions || []), transaction],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });
            }

            return {
                error: error.response?.data?.message || "Cable TV purchase failed"
            };
        }
    };

    const generateRechargeCardPin = async (network, denomination, quantity, totalCost) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Sending ePin generation request with:", {
                network,
                denomination,
                quantity
            });

            const res = await axios.post(
                `${BASE_URL}/generate-epin`,
                {
                    network,
                    denomination,
                    quantity,
                    totalCost
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const { balance: newBalance, transaction } = res.data;

            console.log("✅ ePin generation successful:", res.data);

            // ✅ Update local balance & transactions
            setCurrentUser((prevUser) => {
                const updatedUser = {
                    ...prevUser,
                    balance: newBalance,
                    transactions: [...(prevUser.transactions || []), transaction],
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                return updatedUser;
            });

            return res.data;

        } catch (error) {
            console.error("❌ generateRechargeCardPin failed:", error.response?.data);

            const transaction = error.response?.data?.transaction;
            const newBalance = error.response?.data?.balance;

            // 🟡 Update local state if backend returned transaction info
            if (transaction) {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        transactions: [...(prevUser.transactions || []), transaction],
                    };

                    if (typeof newBalance !== "undefined") {
                        updatedUser.balance = newBalance;
                    }

                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });
            }

            return {
                error: error.response?.data?.message || "Failed to generate recharge pins",
            };
        }
    };


    const fetchGeneratedPins = async () => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            const res = await axios.get(`${BASE_URL}/pins`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("📦 All Generated Pins Fetched:", res.data.pins);

            return res.data.pins;
        } catch (error) {
            console.error("❌ Failed to fetch generated pins:", error.response?.data || error.message);
            return { error: error.response?.data?.message || "Something went wrong" };
        }
    };

    // ✅ NEW: Update pin status
    const updatePinStatus = async (pinId, newStatus) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(
                `${BASE_URL}/update-pin-status`,
                { pinId, newStatus },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data; // { message, pin }
        } catch (error) {
            console.error("Error updating pin status:", error);
            return { error: "Failed to update pin status" };
        }
    };

    const purchaseEducationToken = async (eduType, price) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Sending Education Token Purchase Request:", {
                eduType,
                price,
            });

            const res = await axios.post(
                `${BASE_URL}/education-token`, // your backend route
                {
                    eduType,
                    price,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            console.log("✅ Education Token Purchase Response:", res.data);

            if (res.data.token) {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        balance: res.data.balance || prevUser.balance,
                        transactions: [
                            ...(prevUser.transactions || []),
                            res.data.transaction,
                        ],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });

                return res.data; // Contains token, transaction, updated balance
            } else {
                return { error: res.data.message || "Failed to purchase token" };
            }

        } catch (error) {
            console.error("❌ Failed to Purchase Education Token!", error.response?.data);
            return { error: error.response?.data?.message || "Something went wrong" };
        }
    };

    const fetchCableDetails = async () => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            const res = await axios.get(`${BASE_URL}/cable-tv-subscriptions`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("🎓 All Cable Subscriptions Fetched:", res.data.tokens);

            return res.data.tokens;
        } catch (error) {
            console.error("❌ Failed to fetch cable subscription details:", error.response?.data || error.message);
            return { error: error.response?.data?.message || "Something went wrong" };
        }
    };


    const fetchEducationDetails = async () => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            const res = await axios.get(`${BASE_URL}/edu-purchases`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("🎓 All Education Tokens Fetched:", res.data.tokens);

            return res.data.tokens;
        } catch (error) {
            console.error("❌ Failed to fetch education details:", error.response?.data || error.message);
            return { error: error.response?.data?.message || "Something went wrong" };
        }
    };


    const verifyElectricityCustomer = async (service, meterNumber, meterType) => {

        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Verifying Electricity Customer:", {

                service,
                meterNumber,
                meterType,
            });

            const res = await axios.get(`${BASE_URL}/verify-electricity`, {
                params: {

                    service,
                    meterNumber,
                    meterType,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("✅ Verification Success:", res.data);

            if (res.data.status === "success") {
                return res.data; // includes customerName, accessToken
            } else {
                return { error: res.data.message || "Verification failed" };
            }

        } catch (error) {
            console.error("❌ Verification Failed:", error.response?.data);
            return { error: error.response?.data?.message || "Something went wrong" };
        }
    };

    const payElectricityBill = async (service, meterNumber, meterType, accessToken, amount) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("⚡ Initiating Electricity Payment:", {
                service,
                meterNumber,
                meterType,
                amount,
            });

            const res = await axios.post(
                `${BASE_URL}/pay-electricity`,
                {
                    service,
                    meterNumber,
                    meterType,
                    accessToken,
                    amount,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            console.log("✅ Electricity Payment Response:", res.data);

            const { transaction, balance, token: meterToken } = res.data;

            // ✅ Update current user with new balance and transaction
            setCurrentUser((prevUser) => {
                const updatedUser = {
                    ...prevUser,
                    balance: balance || prevUser.balance,
                    transactions: [
                        ...(prevUser.transactions || []),
                        transaction,
                    ],
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                return updatedUser;
            });

            return {
                ...res.data,
                meterToken
            };

        } catch (error) {
            console.error("❌ Electricity Payment Failed!", error.response?.data);

            const transaction = error.response?.data?.transaction;
            const balance = error.response?.data?.balance;

            if (transaction) {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        balance: balance || prevUser.balance,
                        transactions: [
                            ...(prevUser.transactions || []),
                            transaction,
                        ],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });
            }

            return { error: error.response?.data?.message || "Payment failed" };
        }
    };

    const fetchElectricityDetails = async () => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            const res = await axios.get(`${BASE_URL}/electricity-purchases`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("🎓 All Electricity Tokens Fetched:", res.data.tokens);

            return res.data.tokens;
        } catch (error) {
            console.error("❌ Failed to fetch electricity details:", error.response?.data || error.message);
            return { error: error.response?.data?.message || "Something went wrong" };
        }
    };

    /*const verifyAirtimeToCashAvailability = async (network) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📡 Verifying Airtime to Cash availability:", { network });

            const res = await axios.get(`/airtime-to-cash/verify`, {
                params: {
                   
                    network,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("✅ Airtime to Cash Verification Response:", res.data);

            if (res.data.status === "Completed") {
                console.log("yes")
                return res.data.description;
            } else {
                console.log("no")
                return { error: res.data?.description?.message || "Servicessss not available" };
            }

        } catch (error) {
            console.error("❌ Airtime to Cash Verification Failed:", error.response?.data || error.message);
            return { error: error.response?.data?.description || "Something went wrong" };
        }
    };

    const convertAirtimeToCash = async ({
        network,
        sender,
        sendernumber,
        amount,
        sitephone,
    }) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("💸 Converting Airtime to Cash:", {
              
                network,
                sender,
                sendernumber,
                amount,
                sitephone,
            });

            const res = await axios.post(
                `/airtime-to-cash`,
                {
                    network,
                    sender,
                    sendernumber,
                    amount,
                    sitephone,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("✅ Airtime to Cash Conversion Response:", res.data);

            if (res.data.status === "Processing") {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        transactions: [
                            ...(prevUser.transactions || []),
                            res.data,
                        ],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });

                return res.data;
            } else {
                return { error: res.data?.description?.message || "Conversion failed" };
            }

        } catch (error) {
            console.error("❌ Airtime to Cash Conversion Failed:", error.response?.data || error.message);
            return { error: error.response?.data?.description || "Something went wrong" };
        }
    };
    */

    const sendBulkSms = async ({ message, sendto, sender, totalCost }) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Sending Bulk SMS:", {

                message,
                sendto,
                sender,
            });

            const res = await axios.post(
                `${BASE_URL}/send-bulk-sms`,
                {
                    message,
                    sendto,
                    sender,
                    totalCost
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("✅ Bulk SMS Response:", res.data);

            const { transaction, balance, status } = res.data;

            if (transaction && (status === "Successful" || status === "Completed")) {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        balance: balance ?? prevUser.balance, // Use backend balance if provided
                        transactions: [...(prevUser.transactions || []), transaction],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });

                return res.data;
            } else {
                return {
                    error: res.data?.description?.message || res.data?.message || "Bulk SMS send failed",
                };
            }

        } catch (error) {
            console.error("❌ Bulk SMS Failed:", error.response?.data || error.message);

            const transaction = error.response?.data?.transaction;
            const balance = error.response?.data?.balance;

            if (transaction) {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        balance: balance ?? prevUser.balance,
                        transactions: [...(prevUser.transactions || []), transaction],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });
            }

            return {
                error: error.response?.data?.message || "Something went wrong while sending SMS",
            };
        }
    };

    /*const buyAirtimeVTU = async ({ network, phone, amount }) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📤 Buying Airtime:", {
                network,
                phone,
                amount,
            });

            const res = await axios.post(
                `${BASE_URL}/buy-airtime`,
                {
                    network,
                    amount,
                    phoneNumber: phone
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("✅ Airtime Purchase Response:", res.data);

            const { transaction, balance, status } = res.data;

            if (transaction && (status === "Successful" || status === "Completed")) {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        balance: balance ?? prevUser.balance,
                        transactions: [...(prevUser.transactions || []), transaction],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });

                return res.data;
            } else {
                return {
                    error:
                        res.data?.description?.message ||
                        res.data?.message ||
                        "Airtime purchase failed",
                };
            }
        } catch (error) {
            console.error("❌ Airtime Purchase Failed:", error.response?.data || error.message);

            const transaction = error.response?.data?.transaction;
            const balance = error.response?.data?.balance;

            if (transaction) {
                setCurrentUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        balance: balance ?? prevUser.balance,
                        transactions: [...(prevUser.transactions || []), transaction],
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    return updatedUser;
                });
            }

            return {
                error:
                    error.response?.data?.message ||
                    "Something went wrong while processing airtime purchase",
            };
        }
    };

    */



    const buySmartData = async (network, dataPlan, planSize, phoneNumber, price) => {
        const token = localStorage.getItem("token");
        if (!token || !currentUser) return { error: "User not logged in" };

        try {
            console.log("📡 Sending buyData request with:", {
                network,
                dataPlan,
                phoneNumber,
                price
            });

            const res = await axios.post(
                `${BASE_URL}/smartdata`,
                {
                    network,
                    dataPlan,
                    planSize,
                    phoneNumber,
                    price
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const { newBalance, transaction } = res.data;

            console.log("✅ Data purchase successful:", res.data);

            // ✅ Update local balance & transactions
            setCurrentUser((prevUser) => {
                const updatedUser = {
                    ...prevUser,
                    balance: newBalance,
                    transactions: [...(prevUser.transactions || []), transaction],
                };
                localStorage.setItem("user", JSON.stringify(updatedUser));
                return updatedUser;
            });

            return res.data;

        } catch (error) {
            if (error.response?.status === 206) {
                const transaction = error.response?.data?.transaction;
                const newBalance = error.response?.data?.newBalance;

                if (transaction) {
                    setCurrentUser((prevUser) => {
                        const updatedUser = {
                            ...prevUser,
                            transactions: [...(prevUser.transactions || []), transaction],
                        };
                        if (typeof newBalance !== "undefined") {
                            updatedUser.balance = newBalance;
                        }
                        localStorage.setItem("user", JSON.stringify(updatedUser));
                        return updatedUser;
                    });
                }

                return { pending: true, message: "Your transaction is still processing…" };
            }

            return { error: error.response?.data?.message || "Failed to buy data" };
        }

    };



    return (
        <AuthContext.Provider value={{
            currentUser,
            notifications,
            resetTransactionPinWithOtp,
            sendOtpForTransactionPinReset,
            changeTransactionPin,
            // verifyAirtimeOtp,
            requestPasswordResetOtp,
            resetPasswordWithOtp,
            // sendAirtimeOtp,
            // sendAirtimeToCash,
            fetchNotifications,
            fetchCurrentUser,
            setNotifications,
            unreadCount,
            selectedTransaction,
            isDarkMode,
            setIsDarkMode,
            setSelectedTransaction,
            verifyOtp,
            resendOtp,
            getTransactionById,
            getPalmPayBanks,
            queryBankAccountName,
            getBanks,
            addBankAccount,
            fetchBankAccounts,
            palmPayPayout,
            markAsRead, updateUserProfile,
            updatePinStatus,
            fetchGeneratedPins,
            generateRechargeCardPin,
            buyCableTV, reportBug,
            register,
            login,
            logout,
            getTransactions,
            buyAirtime,
            addFunds,
            setTransactionPin,
            verifyTransactionPin,
            purchaseEducationToken,
            showPinModal,
            setShowPinModal,
            fetchEducationDetails,
            verifyElectricityCustomer,
            payElectricityBill,
            fetchElectricityDetails,
            sendBulkSms,
            // buyAirtimeVTU,
            fetchCableDetails,
            buySmartData,
            loading

        }}>
            {children}
        </AuthContext.Provider>
    );
};
